import type {
  ChangesetHistory,
  ConfigurationFunctionValueInOut,
  FunctionDescriptorOutput,
  TemplateFieldValueInOut,
  ValueSetupInOut,
} from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import type { Except, Optional } from '@festina-life/core'

import { isSpecialCasedSubTemplatedValueSetup } from '../components/specialCasedSubTemplatedViewers/types'
import type {
  FieldOrigin,
  UserActionData,
  VersionedValue,
  WithOptionalOriginInfo,
} from '../uiModels/types'
import type { DateInterval } from './common'
import type { ComparableField } from './diffing'
import type { MetaType, ModelMetaType, PropertyDescriptor } from './metaModel'

export type TemplateReferenceNodeFull = {
  id: string
  typeName: string
  type: Optional<ModelMetaType>
  referencedBy: TemplateFieldReferenceFull[]
  versions: TemplateVersionReferenceNodeFull[]
  block: string
  layer: LayerKey
}

export type TemplateVersionReferenceNodeFull = {
  owningNode: TemplateReferenceNodeFull
  activeInterval: DateInterval

  fields: TemplateFieldReferenceFull[]
}

export type TemplateFieldReferenceFull = {
  fieldName: string
  owningVersion: TemplateVersionReferenceNodeFull

  referencedType: Optional<ModelMetaType>
  referencedTypeName: Optional<string>

  referencedNodes: TemplateReferenceNodeFull[]
}

export type TemplateReferenceNode = {
  id: string
  typeName: string
  type: Optional<ModelMetaType>
  referencedBy: TemplateFieldReference[]
  fields: TemplateFieldReference[]
  block: string
  layer: LayerKey
}

export type TemplateFieldReference = {
  fieldName: string
  owningNode: TemplateReferenceNode
  referencedType: Optional<ModelMetaType>
  referencedTypeName: string
  referencedNodes: TemplateReferenceNode[]
}

export type TemplateVersionInfo = {
  package: Optional<string[]>
  operationKey: Optional<string>
  changesetHistory: Optional<ChangesetHistory>
  identifier: string
  parentId: Optional<string>
  parent: Optional<TemplateVersionInfo>
  replacesId: Optional<string>
  replaces: Optional<TemplateVersionInfo>
  replacedByIds: Optional<string[]>
  replacedBy: Optional<TemplateVersionInfo[]>
  childrenIds: string[]
  children: TemplateVersionInfo[]
  fromDate: Optional<string>
  block: string
  layer: LayerKey
  isDraft: boolean // True for template version created inside a workspace
  isMissing: boolean // True if the template create operation is not found in the workspace or branch
  isHideVersion: boolean // True if the template version is a hide operation
  actions: UserActionData[]
}

export const areVersionsEqual = (
  v1: TemplateVersionInfo,
  v2: TemplateVersionInfo,
) =>
  v1.identifier === v2.identifier &&
  v1.fromDate === v2.fromDate &&
  v1.layer.equals(v2.layer)

export type TemplateFunctions = {
  declaredFunctions?: Optional<DeclaredConfigurationTemplateFunctionValue[]>
  dynamicFunctions?: Optional<DynamicConfigurationTemplateFunctionValue[]>
}

export type AbstractTemplate = {
  origin?: Optional<FieldOrigin>
  path: string
  type: Optional<ModelMetaType>
  typeName: string
  documentation?: Optional<VersionedValue<string>>
  modelType?: Optional<ModelMetaType>
  modelTypeName: string
  fields: TemplateField[]
} & TemplateFunctions

export type TemplateVersion = Except<
  AbstractTemplate,
  'origin' | 'modelTypeName' | 'typeName'
> & {
  replaces?: Optional<string>
  origin: FieldOrigin
  versionInfo: TemplateVersionInfo
  layer: LayerKey
  block: string
  updateTemplate: (nextVersion: TemplateVersion) => Promise<void>
  isFlattened: boolean
  // Hidden template version will not have a model type name nor a type name
  modelTypeName: Optional<string>
  typeName: Optional<string>
}

export type SubTemplateFieldValue = AbstractTemplate & {
  type?: Optional<ModelMetaType>
  parent?: Optional<VersionedValue<string>>
  subTemplatedField?: Optional<SubTemplateValueSetupField>
  subTemplateIdentifier: string
  defaultSelected?: Optional<ConfigurationFieldExpressionValue>
  selectable?: Optional<ConfigurationFieldExpressionValue>
}

export type ValueConfig = NonNullable<ValueSetupInOut['valueConfig']>

export type ValueSetupFieldValue = {
  documentation?: Optional<VersionedValue<string>>
  valueConfig?: Optional<VersionedValue<ValueConfig>>
  required?: Optional<ConfigurationFieldExpressionValue>
  requiredInModelInstance?: Optional<ConfigurationFieldExpressionValue>
  validations?: Optional<ValidationFieldValue[]>
  path: string
  fieldType: ConfigurationFieldType
  origin?: Optional<FieldOrigin>
}

export const isSubTemplatedFieldValue = (
  value: Optional<Record<string, unknown>>,
): value is SubTemplateFieldValue => !!value && 'subTemplateIdentifier' in value

export const isValueSetupFieldValue = (
  value: Optional<Record<string, unknown>>,
): value is ValueSetupFieldValue => !!value && 'fieldType' in value

export function assertValueSetupFieldValue(
  value: Optional<Record<string, unknown>>,
): asserts value is ValueSetupFieldValue {
  if (!value || !('valueConfig' in value)) {
    throw new Error(
      `Invalid value type. Expected ValueSetupFieldValue, got ${JSON.stringify(
        value,
      )}`,
    )
  }
}

export function assertSubTemplatedField(
  value: TemplateField,
): asserts value is SubTemplateValueSetupField {
  if (!isSubTemplatedField(value)) {
    throw new Error(
      `Invalid value type. Expected CompositeTemplateField, got ${value.fieldType}`,
    )
  }
}

export function assertSimpleValueSetupField(
  field: TemplateField,
): asserts field is SimpleValueSetupField {
  if (field.fieldType !== ConfigurationFieldType.SimpleValueSetup) {
    throw new Error(
      `Invalid field type, expected SimpleValueSetup and received type ${field.fieldType}`,
    )
  }
}

export function assertTemplateReferenceValueSetupField(
  field: TemplateField,
): asserts field is TemplateReferenceValueSetupField {
  if (field.fieldType !== ConfigurationFieldType.TemplateValueSetup) {
    throw new Error(
      `Invalid field type, expected TemplateValueSetup and received type ${field.fieldType}`,
    )
  }
}

export type ConfigurationFieldExpressionValue = Omit<
  ConfigurationSimpleValueFieldValue,
  'valueConfig' | 'required' | 'requiredInModelInstance' | 'validations'
>

export type TemplateReferenceValueSetupFieldValue = ValueSetupFieldValue & {
  variants: TemplateReference[]
  cardinality?: Optional<ConfigurationFieldExpressionValue>
}

export type TemplateReference = {
  defaultSelected?: Optional<ConfigurationFieldExpressionValue>
  selectable?: Optional<ConfigurationFieldExpressionValue>
  cardinality?: Optional<ConfigurationFieldExpressionValue>
  source?: Optional<string>
  path: string
  key?: Optional<string>
  origin?: FieldOrigin
}

export const ValueSetupRole = {
  SIMPLE: 'simple',
  REFERENCE: 'reference',
  SUB_CLASSED_SUB_TEMPLATE: 'subClassedSubTemplate',
  SINGLE_CLASS_SUB_TEMPLATE: 'singleClassSubTemplate',
  IDENTIFIABLE_SUB_TEMPLATE: 'identifiableSubTemplate',
} as const
export type ValueSetupRole =
  (typeof ValueSetupRole)[keyof typeof ValueSetupRole]

export type SubTemplateValueSetupFieldValue = ValueSetupFieldValue & {
  cardinality?: Optional<ConfigurationFieldExpressionValue>
  variants: SubTemplateFieldValue[]
  availableVariantTypes: ModelMetaType[]
}

export type SimpleValueSetupFrameValue =
  | EnumeratedValueFrameFieldValue
  | RangedValueFrameFieldValue

export type SimpleValueSetupFieldValue = ValueSetupFieldValue & {
  defaultValue?: Optional<ConfigurationFieldExpressionValue>
  frame?: Optional<SimpleValueSetupFrameValue>
  dynamicFunctions?: Optional<DynamicConfigurationTemplateFunctionValue[]>
  role: ValueSetupRole
}

export type EnumeratedValueFrameFieldValue = WithOptionalOriginInfo<{
  type: typeof FrameType.EnumeratedValueFrame
  entries?: Optional<ConfigurationFieldExpressionValue>
}>

export type RangedValueFrameFieldValue = WithOptionalOriginInfo<{
  type: typeof FrameType.RangedValueFrame
  range?: Optional<ConfigurationFieldExpressionValue>
}>

export type ValidationFieldValue = {
  path: string
  validation?: Optional<ConfigurationFieldExpressionValue>
  key?: Optional<ConfigurationFieldExpressionValue>
  defaultPattern?: Optional<ConfigurationFieldExpressionValue>
  keyArguments?: Optional<ConfigurationFieldExpressionValue>
  origin: FieldOrigin
}

export type ConfigurationSimpleValueFieldValue = Omit<
  ValueSetupFieldValue,
  'required' | 'requiredInModelInstance' | 'validations' | 'valueConfig'
> & {
  expression?: FPlusExpressionValue
}

export type ConfigurationTemplateFunctionValueCommonFields = {
  path: string
  functionIdentifier: string
  documentation?: Optional<VersionedValue<string>>
  function?: FPlusExpressionValue
  origin?: Optional<FieldOrigin>
  updateRaw: (nextValue: ConfigurationFunctionValueInOut) => void
  isParentNotRelevant: boolean
}

export type DeclaredConfigurationTemplateFunctionValue =
  ConfigurationTemplateFunctionValueCommonFields & {
    type: 'declared'
    descriptor: Optional<FunctionDescriptorOutput>
    update: (
      nextValue: Optional<DeclaredConfigurationTemplateFunctionValue>,
    ) => void
  }

export type DynamicConfigurationTemplateFunctionValue =
  ConfigurationTemplateFunctionValueCommonFields & {
    type: 'dynamic'
    update: (
      nextValue: Optional<DynamicConfigurationTemplateFunctionValue>,
    ) => void
  }

export type ConfigurationTemplateFunctionValue =
  | DeclaredConfigurationTemplateFunctionValue
  | DynamicConfigurationTemplateFunctionValue

export type SupportedEditableTemplateFieldValue =
  | SimpleValueSetupFieldValue
  | TemplateReferenceValueSetupFieldValue
  | ConfigurationSimpleValueFieldValue
  | SubTemplateValueSetupFieldValue

export type AbstractTemplateField<
  T extends SupportedEditableTemplateFieldValue,
> = {
  name: string
  path: string
  //The template type this field lies on
  parentTemplateType: ModelMetaType
  //The type if any that this field is generated from, empty if we are generating from the class configuration, and the type is an F+ type
  originalPropertyType?: Optional<MetaType>
  //The template property
  templatePropertyDescriptor: Optional<PropertyDescriptor>
  //The model property if one exists, empty if the type is configuration only or if its on the class configuration
  modelPropertyDescriptor?: Optional<PropertyDescriptor>
  role: ValueSetupRole
  update: (nextValue: Optional<T>) => Promise<void>
  updateRaw: (nextValue: TemplateFieldValueInOut) => Promise<void>
  // Will be undefined for empty fields shown in the "Show empty" view.
  origin?: Optional<FieldOrigin>
  isParentNotRelevant: boolean
}

export type SimpleTemplateField =
  | TemplateReferenceValueSetupField
  | SimpleValueSetupField
  | ConfigurationSimpleValueField

export type TemplateField = SimpleTemplateField | SubTemplateValueSetupField
export type NonConfigurationSimpleValueField =
  | SubTemplateValueSetupField
  | TemplateReferenceValueSetupField
  | SimpleValueSetupField

export const isSimpleTemplateField = (
  field: TemplateField,
): field is SimpleTemplateField =>
  SimpleFieldTypeEnums.includes(field.fieldType)

export const isSimpleValueSetupField = (
  field: TemplateField,
): field is SimpleValueSetupField =>
  field.fieldType === ConfigurationFieldType.SimpleValueSetup

export const isConfigurationValueSetupField = (
  field: TemplateField,
): field is ConfigurationSimpleValueField =>
  field.fieldType === ConfigurationFieldType.ConfigurationExpressionValue

export const isTemplateReferenceValueSetupField = (
  field: TemplateField,
): field is TemplateReferenceValueSetupField =>
  field.fieldType === ConfigurationFieldType.TemplateValueSetup

export const isSimpleOrConfigurationOnlyFieldValueField = (
  value: TemplateField,
): value is SimpleValueSetupField | ConfigurationSimpleValueField =>
  isSimpleValueSetupField(value) || isConfigurationValueSetupField(value)

export type TemplateFieldWithCardinality =
  | TemplateReferenceValueSetupField
  | SubTemplateValueSetupField

export const isFieldWithCardinality = (
  field: TemplateField,
): field is TemplateFieldWithCardinality =>
  field.fieldType === ConfigurationFieldType.TemplateValueSetup ||
  field.fieldType === ConfigurationFieldType.SubTemplateValueSetup

export const isSubTemplateValueSetupField = (
  field: TemplateField,
): field is SubTemplateValueSetupField =>
  field.fieldType === ConfigurationFieldType.SubTemplateValueSetup

export type SimpleValueSetupField =
  AbstractTemplateField<SimpleValueSetupFieldValue> & {
    fieldType: typeof ConfigurationFieldType.SimpleValueSetup
    value: Optional<SimpleValueSetupFieldValue>
  }

export type SubTemplateValueSetupField =
  AbstractTemplateField<SubTemplateValueSetupFieldValue> & {
    fieldType: typeof ConfigurationFieldType.SubTemplateValueSetup
    value: Optional<SubTemplateValueSetupFieldValue>
    isRenderingEmpty?: boolean
  }

export type TemplateReferenceValueSetupField =
  AbstractTemplateField<TemplateReferenceValueSetupFieldValue> & {
    fieldType: typeof ConfigurationFieldType.TemplateValueSetup
    value: Optional<TemplateReferenceValueSetupFieldValue>
  }

export type ConfigurationSimpleValueField =
  AbstractTemplateField<ConfigurationSimpleValueFieldValue> & {
    fieldType: typeof ConfigurationFieldType.ConfigurationExpressionValue
    value: Optional<ConfigurationSimpleValueFieldValue>
  }

export type FPlusExpressionValue = {
  fplusSource?: Optional<string>
}

export const ConfigurationFieldType = {
  SimpleValueSetup: 'SimpleValueSetup',
  SubTemplateValueSetup: 'SubTemplateValueSetup',
  TemplateValueSetup: 'TemplateValueSetup',
  ConfigurationExpressionValue: 'ConfigurationExpressionValue',
} as const
export type ConfigurationFieldType =
  (typeof ConfigurationFieldType)[keyof typeof ConfigurationFieldType]

export const MetaModelConfigurationTypeNameConfigurationFieldType: Record<
  string,
  ConfigurationFieldType
> = {
  SimpleValueSetup: ConfigurationFieldType.SimpleValueSetup,
  SingleSubTemplateValueSetup: ConfigurationFieldType.SubTemplateValueSetup,
  MultipleSubTemplateValueSetup: ConfigurationFieldType.SubTemplateValueSetup,
  TemplateValueSetup: ConfigurationFieldType.TemplateValueSetup,
  ConfigurationExpressionValue:
    ConfigurationFieldType.ConfigurationExpressionValue,
}

export const ConfigurationFieldTypeToInOutMapper: Record<
  ConfigurationFieldType,
  string
> = {
  [ConfigurationFieldType.SimpleValueSetup]: 'SimpleValueSetupInOut',
  [ConfigurationFieldType.TemplateValueSetup]: 'TemplateValueSetupInOut',
  [ConfigurationFieldType.ConfigurationExpressionValue]:
    'ConfigurationExpressionValueInOut',
  [ConfigurationFieldType.SubTemplateValueSetup]: 'SubTemplateValueSetupInOut',
}

export const FrameType = {
  RangedValueFrame: 0,
  EnumeratedValueFrame: 1,
} as const
export type FrameType = (typeof FrameType)[keyof typeof FrameType]

export const SimpleFieldTypeEnums: ConfigurationFieldType[] = [
  ConfigurationFieldType.SimpleValueSetup,
  ConfigurationFieldType.TemplateValueSetup,
  ConfigurationFieldType.ConfigurationExpressionValue,
]

/**
 * Type guard used to differentiate configuration function entries from SimpleTemplateFields and TemplateVersions
 */
export const isConfigurationFunction = (
  configurationValue: ComparableField,
): configurationValue is ConfigurationTemplateFunctionValue =>
  !isSubTemplatedFieldValue(configurationValue) &&
  !isSpecialCasedSubTemplatedValueSetup(configurationValue) &&
  !isTemplateVersion(configurationValue) &&
  'type' in configurationValue &&
  ['declared', 'dynamic'].includes(configurationValue.type)

/**
 * Type guard used to differentiate template versions from template fields and configuration functions.
 */
export const isTemplateVersion = (
  value: ComparableField,
): value is TemplateVersion => 'updateTemplate' in value

/**
 * Type guard used to differentiate template fields from template versions and configuration functions.
 */
export const isTemplateField = (value: unknown): value is TemplateField =>
  typeof value === 'object' &&
  !Array.isArray(value) &&
  !!value &&
  'name' in value &&
  'role' in value

export const isSubTemplatedField = (
  value: Optional<TemplateField>,
): value is SubTemplateValueSetupField =>
  value?.fieldType === ConfigurationFieldType.SubTemplateValueSetup

export const isDeclaredConfigurationTemplateFunctionValue = (
  value: ConfigurationTemplateFunctionValue,
): value is DeclaredConfigurationTemplateFunctionValue =>
  value.type === 'declared'

export const isEnumeratedValueFrameFieldValue = (
  frame: SimpleValueSetupFrameValue,
): frame is EnumeratedValueFrameFieldValue =>
  frame.type === FrameType.EnumeratedValueFrame

export const isRangedValueFrameFieldValue = (
  frame: SimpleValueSetupFrameValue,
): frame is RangedValueFrameFieldValue =>
  frame.type === FrameType.RangedValueFrame

export const isNotRelevantField = (field: Optional<TemplateField>): boolean =>
  !!field &&
  ((isValueSetupFieldValue(field.value) &&
    field.value.valueConfig?.value === 'notRelevant') ||
    !!field.isParentNotRelevant)
